import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { 
  Upload, 
  File, 
  Shield, 
  ShieldAlert, 
  Clock, 
  User, 
  AlertTriangle,
  CheckCircle,
  Download,
  Trash2,
  Eye,
  Filter,
  ExternalLink
} from 'lucide-react';
import FileUpload from '../../Beehive/FileUpload';
import { OnboardingAttachment } from './types';
import { ApiRoutes } from '../../../api/routes';

interface FilesPanelProps {
  projectId: string;
  userRole: 'client' | 'vendor' | 'admin';
}

interface QuarantinedFile {
  id: string;
  scanId: string;
  filename: string;
  uploadedBy: string;
  uploadedAt: string;
  threatName: string;
  signature: string;
  size: number;
}

export const FilesPanel: React.FC<FilesPanelProps> = ({ projectId, userRole }) => {
  const [activeTab, setActiveTab] = useState<'uploads' | 'quarantined'>('uploads');
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [projectFiles, setProjectFiles] = useState<OnboardingAttachment[]>([]);
  const [quarantinedFiles, setQuarantinedFiles] = useState<QuarantinedFile[]>([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState<'all' | 'client-upload' | 'pm-update' | 'conversion-mapping'>('all');

  useEffect(() => {
    loadProjectFiles();
    if (userRole === 'admin') {
      loadQuarantinedFiles();
    }
  }, [projectId, userRole]);

  const loadProjectFiles = async () => {
    try {
      setLoading(true);
      const response = await ApiRoutes.getProjectFiles(projectId);
      if (response.success && response.data) {
        setProjectFiles(response.data);
      } else {
        console.error('Failed to load project files:', response.error);
        // Fallback to mock data if API fails
        setProjectFiles([
          {
            id: 'att-001',
            projectId,
            name: 'Tyler_ERP_Requirements.pdf',
            description: 'ERP system requirements document',
            size: 2048576,
            uploadedBy: { id: 'user-001', name: 'John Smith', email: 'john.smith@tylertech.com' },
            status: 'clean',
            uploadedAt: '2024-08-24T10:30:00Z',
            sharepointUrl: 'https://tylertech.sharepoint.com/sites/projects/ERP_Implementation/Shared%20Documents/01_Project_Timeline/Tyler_ERP_Requirements.pdf'
          },
          {
            id: 'att-002',
            projectId,
            name: 'Data_Migration_Plan.xlsx',
            description: 'Data migration strategy and timeline',
            size: 1024000,
            uploadedBy: { id: 'user-002', name: 'Sarah Johnson', email: 'sarah.johnson@tylertech.com' },
            status: 'clean',
            uploadedAt: '2024-08-23T14:15:00Z',
            sharepointUrl: 'https://tylertech.sharepoint.com/sites/projects/ERP_Implementation/Shared%20Documents/03_Conversion_Mapping/Data_Migration_Plan.xlsx'
          }
        ]);
      }
    } catch (error) {
      console.error('Failed to load project files:', error);
      // Fallback to mock data
      setProjectFiles([
        {
          id: 'att-001',
          projectId,
          name: 'Tyler_ERP_Requirements.pdf',
          description: 'ERP system requirements document',
          size: 2048576,
          uploadedBy: { id: 'user-001', name: 'John Smith', email: 'john.smith@tylertech.com' },
          status: 'clean',
          uploadedAt: '2024-08-24T10:30:00Z',
          sharepointUrl: 'https://tylertech.sharepoint.com/sites/projects/ERP_Implementation/Shared%20Documents/01_Project_Timeline/Tyler_ERP_Requirements.pdf'
        },
        {
          id: 'att-002',
          projectId,
          name: 'Data_Migration_Plan.xlsx',
          description: 'Data migration strategy and timeline',
          size: 1024000,
          uploadedBy: { id: 'user-002', name: 'Sarah Johnson', email: 'sarah.johnson@tylertech.com' },
          status: 'clean',
          uploadedAt: '2024-08-23T14:15:00Z',
          sharepointUrl: 'https://tylertech.sharepoint.com/sites/projects/ERP_Implementation/Shared%20Documents/03_Conversion_Mapping/Data_Migration_Plan.xlsx'
        }
      ]);
    } finally {
      setLoading(false);
    }
  };

  const loadQuarantinedFiles = async () => {
    try {
      const response = await fetch('/api/security/quarantine');
      if (response.ok) {
        const files = await response.json();
        setQuarantinedFiles(files);
      }
    } catch (error) {
      console.error('Failed to load quarantined files:', error);
      setQuarantinedFiles([
        {
          id: 'quar-001',
          scanId: 'scan-12345',
          filename: 'suspicious_document.pdf',
          uploadedBy: 'Unknown User',
          uploadedAt: '2024-08-24T08:20:00Z',
          threatName: 'Trojan.Generic.KD.12345',
          signature: 'malware-signature-abc123',
          size: 1536000
        }
      ]);
    }
  };

  const handleFileUpload = async (files: File[]) => {
    await loadProjectFiles();
    setShowUploadModal(false);
  };

  const handleReleaseQuarantined = async (scanId: string) => {
    try {
      const response = await fetch(`/api/security/quarantine/${scanId}/release`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' }
      });
      if (response.ok) {
        await loadQuarantinedFiles();
      }
    } catch (error) {
      console.error('Failed to release file:', error);
    }
  };

  const handleDeleteQuarantined = async (scanId: string) => {
    try {
      const response = await fetch(`/api/security/quarantine/${scanId}`, {
        method: 'DELETE',
        headers: { 'Content-Type': 'application/json' }
      });
      if (response.ok) {
        await loadQuarantinedFiles();
      }
    } catch (error) {
      console.error('Failed to delete file:', error);
    }
  };

  const formatFileSize = (bytes: number) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleString();
  };

  const filteredFiles = projectFiles.filter(file => {
    if (filter === 'all') return true;
    return file.source === filter;
  });

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'clean':
        return <CheckCircle className="w-4 h-4 text-green-500" />;
      case 'quarantined':
        return <ShieldAlert className="w-4 h-4 text-red-500" />;
      default:
        return <Clock className="w-4 h-4 text-yellow-500" />;
    }
  };

  return (
    <div className="h-full bg-white">
      <div className="p-8">
        <div className="mb-8">
          <h2 className="text-3xl font-bold bg-gradient-to-r from-[#4267A1] to-[#5A7BC4] bg-clip-text text-transparent mb-4 drop-shadow-sm">
            Files & Documents
          </h2>
          <p className="text-gray-600 text-lg drop-shadow-sm">
            Secure file management with automated scanning and client collaboration
          </p>
        </div>

        {/* Tab Navigation */}
        <div className="flex items-center space-x-1 bg-gray-100 rounded-lg p-1 mb-6 w-fit">
          <button
            onClick={() => setActiveTab('uploads')}
            className={`px-4 py-2 rounded-md text-sm font-medium transition-all duration-200 ${
              activeTab === 'uploads'
                ? 'bg-white text-[#4267A1] shadow-sm'
                : 'text-gray-600 hover:text-gray-900'
            }`}
          >
            <div className="flex items-center space-x-2">
              <File className="w-4 h-4" />
              <span>All Uploads</span>
            </div>
          </button>
          {userRole === 'admin' && (
            <button
              onClick={() => setActiveTab('quarantined')}
              className={`px-4 py-2 rounded-md text-sm font-medium transition-all duration-200 ${
                activeTab === 'quarantined'
                  ? 'bg-white text-red-600 shadow-sm'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              <div className="flex items-center space-x-2">
                <ShieldAlert className="w-4 h-4" />
                <span>Quarantined ({quarantinedFiles.length})</span>
              </div>
            </button>
          )}
        </div>

        {/* Upload Section */}
        {activeTab === 'uploads' && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="space-y-6"
          >
            {/* Upload Box */}
            <div className="tyler-card border-2 border-dashed border-gray-300 p-8 text-center hover:border-[#4267A1] transition-colors">
              <Upload className="w-12 h-12 text-gray-400 mx-auto mb-4" />
              <h3 className="text-lg font-semibold text-gray-900 mb-2 drop-shadow-sm">Upload Files</h3>
              <p className="text-gray-600 mb-4 drop-shadow-sm">
                Drag and drop files here or click to browse
              </p>
              <button
                onClick={() => setShowUploadModal(true)}
                className="px-6 py-3 bg-gradient-to-r from-[#4267A1] to-[#5A7BC4] text-white rounded-lg font-medium hover:shadow-lg transition-all duration-200"
              >
                Choose Files
              </button>
            </div>

            {/* Filter Controls */}
            <div className="flex items-center space-x-4">
              <div className="flex items-center space-x-2">
                <Filter className="w-4 h-4 text-gray-500" />
                <span className="text-sm text-gray-600">Filter by source:</span>
              </div>
              <select
                value={filter}
                onChange={(e) => setFilter(e.target.value as any)}
                className="px-3 py-1 border border-gray-300 rounded-md text-sm focus:ring-2 focus:ring-[#4267A1] focus:border-transparent"
              >
                <option value="all">All Sources</option>
                <option value="client-upload">Client Uploads</option>
                <option value="pm-update">PM Updates</option>
                <option value="conversion-mapping">Conversion Mapping</option>
              </select>
            </div>

            {/* Files Table */}
            <div className="tyler-card overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Name
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Description
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Uploaded By
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Status
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Size
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Uploaded
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        SharePoint
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {loading ? (
                      <tr>
                        <td colSpan={8} className="px-6 py-4 text-center text-gray-500">
                          Loading files...
                        </td>
                      </tr>
                    ) : filteredFiles.length === 0 ? (
                      <tr>
                        <td colSpan={8} className="px-6 py-4 text-center text-gray-500">
                          No files found
                        </td>
                      </tr>
                    ) : (
                      filteredFiles.map((file) => (
                        <tr key={file.id} className="hover:bg-gray-50">
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="flex items-center">
                              <File className="w-5 h-5 text-gray-400 mr-3" />
                              <span className="text-sm font-medium text-gray-900">{file.name}</span>
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                            {file.description || 'No description available'}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="flex items-center">
                              <User className="w-4 h-4 text-gray-400 mr-2" />
                              <span className="text-sm text-gray-900">{file.uploadedBy.name}</span>
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="flex items-center">
                              {getStatusIcon(file.status)}
                              <span className="ml-2 text-sm text-gray-900 capitalize">
                                {file.status}
                              </span>
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                            {formatFileSize(file.size)}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                            {formatDate(file.uploadedAt)}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            {file.sharepointUrl ? (
                              <div className="flex items-center space-x-2">
                                <CheckCircle className="w-4 h-4 text-green-500" />
                                <span className="text-sm text-green-600 font-medium">Verified</span>
                                <a
                                  href={file.sharepointUrl}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="text-[#4267A1] hover:text-[#2E4F7A] transition-colors"
                                  title="Open in SharePoint"
                                >
                                  <ExternalLink className="w-4 h-4" />
                                </a>
                              </div>
                            ) : (
                              <div className="flex items-center space-x-2">
                                <Clock className="w-4 h-4 text-yellow-500" />
                                <span className="text-sm text-yellow-600">Pending</span>
                              </div>
                            )}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                            <div className="flex items-center space-x-2">
                              {file.sharepointUrl ? (
                                <button
                                  onClick={() => window.open(file.sharepointUrl, '_blank')}
                                  className="text-[#4267A1] hover:text-[#2E4F7A] transition-colors"
                                  title="Open in SharePoint"
                                >
                                  <ExternalLink className="w-4 h-4" />
                                </button>
                              ) : (
                                <button
                                  onClick={() => window.open(file.url || '#', '_blank')}
                                  className="text-gray-600 hover:text-gray-900 transition-colors"
                                  title="View"
                                  disabled={!file.url}
                                >
                                  <Eye className="w-4 h-4" />
                                </button>
                              )}
                            </div>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </motion.div>
        )}

        {/* Quarantined Files Tab */}
        {activeTab === 'quarantined' && userRole === 'admin' && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="space-y-6"
          >
            <div className="bg-red-50 border border-red-200 rounded-lg p-4">
              <div className="flex items-center">
                <AlertTriangle className="w-5 h-5 text-red-500 mr-2" />
                <span className="text-red-800 font-medium">
                  Quarantined Files - Admin Access Required
                </span>
              </div>
              <p className="text-red-700 text-sm mt-1">
                These files have been flagged by security scanning and require admin review.
              </p>
            </div>

            <div className="tyler-card overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Filename
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Threat
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Scan ID
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Uploaded By
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Size
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Detected
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {quarantinedFiles.length === 0 ? (
                      <tr>
                        <td colSpan={7} className="px-6 py-4 text-center text-gray-500">
                          <div className="flex flex-col items-center">
                            <Shield className="w-8 h-8 text-green-500 mb-2" />
                            <span>No quarantined files - all uploads are clean</span>
                          </div>
                        </td>
                      </tr>
                    ) : (
                      quarantinedFiles.map((file) => (
                        <tr key={file.id} className="hover:bg-gray-50">
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="flex items-center">
                              <ShieldAlert className="w-5 h-5 text-red-500 mr-3" />
                              <span className="text-sm font-medium text-gray-900">{file.filename}</span>
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <span className="px-2 py-1 text-xs font-medium bg-red-100 text-red-800 rounded-full">
                              {file.threatName}
                            </span>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600 font-mono">
                            {file.scanId}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="flex items-center">
                              <User className="w-4 h-4 text-gray-400 mr-2" />
                              <span className="text-sm text-gray-900">{file.uploadedBy}</span>
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                            {formatFileSize(file.size)}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                            {formatDate(file.uploadedAt)}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                            <div className="flex items-center space-x-2">
                              <button
                                onClick={() => handleReleaseQuarantined(file.scanId)}
                                className="text-green-600 hover:text-green-800 transition-colors px-2 py-1 text-xs bg-green-50 rounded"
                                title="Release from quarantine"
                              >
                                Release
                              </button>
                              <button
                                onClick={() => handleDeleteQuarantined(file.scanId)}
                                className="text-red-600 hover:text-red-800 transition-colors"
                                title="Delete permanently"
                              >
                                <Trash2 className="w-4 h-4" />
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </motion.div>
        )}
      </div>

      {/* Upload Modal */}
      {showUploadModal && (
        <FileUpload
          isOpen={showUploadModal}
          onClose={() => setShowUploadModal(false)}
          projectId={projectId}
          onUploadComplete={handleFileUpload}
        />
      )}
    </div>
  );
};

export default FilesPanel;
